import type { ReactNode } from 'react';

import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';

import { tooltipPlacementClasses } from './ui/classes';

export type TooltipPlacement = keyof typeof tooltipPlacementClasses;

export interface TooltipProps {
  /**
   * Test id for Tooltip
   */
  testId?: string;
  /**
   * Node with tooltip children
   */
  children: ReactNode;
  /**
   * Node with tooltip element
   */
  tooltip?: ReactNode;
  /**
   * Tooltip placement variant
   */
  tooltipPlacement?: TooltipPlacement;
  /**
   * Tooltip placement variant for phone (under md)
   */
  phoneTooltipPlacement?: TooltipPlacement;
  /**
   * Additional classes for tooltip group
   */
  additionalClasses?: string;
  /**
   * Additional classes for tooltip
   */
  additionalTooltipClasses?: string;
}

const Tooltip = ({
  testId = '',
  children,
  tooltip,
  tooltipPlacement = 'topCenter',
  phoneTooltipPlacement = 'topCenter',
  additionalClasses = '',
  additionalTooltipClasses = '',
}: TooltipProps) => {
  const baseTooltipPlacementClasses = useMemo(
    () =>
      tooltipPlacementClasses[tooltipPlacement]
        .split(' ')
        .map((el) => `sm:${el}`)
        .join(' '),
    [tooltipPlacement],
  );

  if (!tooltip) {
    return <>{children}</>;
  }

  return (
    <div
      className={twMerge('relative h-fit group/tooltip', additionalClasses)}
      {...getTestProps(testId, 'Tooltip-container')}
    >
      {children}
      <div
        className={twMerge(
          'absolute rounded-md p-3 w-max bg-slate-600 dark:bg-slate-700 z-20',
          'opacity-0 invisible group-hover/tooltip:visible group-hover/tooltip:opacity-100 text-white text-xs',
          'transition-opacity ease-in-out duration-300 after:content[""] after:absolute after:h-4 after:w-4',
          'after:rotate-45 after:bg-slate-600 dark:after:bg-slate-700 md:p-4 max-w-[200px] md:max-w-xs',
          tooltipPlacementClasses[phoneTooltipPlacement],
          baseTooltipPlacementClasses,
          additionalTooltipClasses,
        )}
        {...getTestProps(testId, 'Tooltip-content')}
      >
        {tooltip}
      </div>
    </div>
  );
};

export default Tooltip;
